/**
 * Second factor for admin and coaching personas.
 *
 * Two shapes, driven by `mfa_enrolled` on the token response:
 *   - not enrolled → `POST /auth/v1/mfa/enroll` returns the otpauth URI and
 *     one-time backup codes, which are shown once, then the code is verified;
 *   - enrolled → straight to the 6-digit code.
 * Verification returns an upgraded access token (`mfa_verified: true`).
 *
 * Security boundary: this card runs on a PARTIAL token (password accepted,
 * factor not yet proven). It therefore never offers to replace an existing
 * authenticator — that recovery affordance belongs only to the signed-in
 * Settings → Preferences page. Here a locked-out user gets guidance instead.
 */
import { useEffect, useState } from 'react';

import { useApiClient } from '../../app/providers';
import { useSession } from '../../app/session-context';
import { TextField } from '../../components/text-field';
import type { MfaEnrollment } from '../../lib/api-client';
import {
  ApiError,
  isSessionError,
  SESSION_EXPIRED_MESSAGE,
  toUserMessage,
} from '../../lib/api-error';

const CODE_PATTERN = /^\d{6}$/;
/** A saved backup code: 8+ letters/digits, hyphens optional (`AB12-CD34`). */
const BACKUP_CODE_PATTERN = /^[0-9a-z]{8,}$/i;

function isAcceptableCode(raw: string, allowsBackupCode: boolean): boolean {
  const trimmed = raw.trim();
  if (CODE_PATTERN.test(trimmed)) return true;
  return allowsBackupCode && BACKUP_CODE_PATTERN.test(trimmed.replace(/-/g, ''));
}

const INVALID_CODE_MESSAGE =
  'That code was not accepted. Check your authenticator app and enter the current 6-digit code.';
const RATE_LIMITED_MESSAGE = 'Too many attempts. Wait a minute, then try again.';

/**
 * Shown under the code field for enrolled accounts. Deliberately no reset
 * link: the only ways past a lost authenticator are a backup code (issued at
 * setup) or a Team Admin who can clear the factor for the account.
 */
/** Field hint for enrolled accounts: both accepted shapes, with an example. */
export const BACKUP_CODE_HINT =
  'The 6-digit code from your authenticator app, or one of your saved backup codes (e.g. AB12-CD34).';

export const MFA_RECOVERY_HELP =
  'Lost your authenticator? Enter one of the backup codes you saved at setup, or ask your Team Admin for account recovery. Authenticators can only be replaced from Settings → Preferences after signing in.';

/**
 * Human-readable text for a failed `POST /auth/v1/mfa/verify`.
 *
 * The banner never repeats a backend `detail` verbatim: the gateway's
 * "This endpoint requires a bearer token." (a missing/expired partial token)
 * becomes a session message, `MFA_INVALID_CODE` a wrong-code hint, and
 * anything unrecognised the generic fallback.
 */
export function describeMfaVerifyError(error: unknown): string {
  if (!ApiError.isApiError(error)) return INVALID_CODE_MESSAGE;
  if (error.isOffline) return error.message;
  if (isSessionError(error)) return SESSION_EXPIRED_MESSAGE;
  switch (error.code) {
    case 'MFA_INVALID_CODE':
      return INVALID_CODE_MESSAGE;
    case 'MFA_NOT_ENROLLED':
      return 'No authenticator is set up for this account yet. Set one up first, then enter its code.';
    case 'RATE_LIMITED':
    case 'HTTP_429':
      return RATE_LIMITED_MESSAGE;
    default:
      if (error.status === 429) return RATE_LIMITED_MESSAGE;
      if (error.status === 401 || error.status === 403) return SESSION_EXPIRED_MESSAGE;
      return toUserMessage(error, INVALID_CODE_MESSAGE);
  }
}

/** Human-readable text for a failed `POST /auth/v1/mfa/enroll`. */
export function describeMfaEnrolError(error: unknown, fallback: string): string {
  if (ApiError.isApiError(error) && error.code === 'MFA_ALREADY_ENROLLED') {
    return 'An authenticator is already set up for this account. Enter its current code below.';
  }
  return toUserMessage(error, fallback);
}

export function LoginMfaStep({
  isEnrolled,
  onVerified,
  onCancel,
}: {
  isEnrolled: boolean;
  onVerified: () => void;
  onCancel: () => void;
}): JSX.Element {
  const apiClient = useApiClient();
  const { applyMfaVerification, signOut } = useSession();
  const [enrollment, setEnrollment] = useState<MfaEnrollment | null>(null);
  const [code, setCode] = useState('');
  const [fieldError, setFieldError] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [isBusy, setIsBusy] = useState(!isEnrolled);

  useEffect(() => {
    if (isEnrolled) return;
    let isCancelled = false;
    apiClient
      .enrollMfa()
      .then((result) => {
        if (!isCancelled) setEnrollment(result);
      })
      .catch((error: unknown) => {
        if (!isCancelled) {
          setFormError(
            describeMfaEnrolError(
              error,
              'Could not start authenticator enrolment. Try signing in again.'
            )
          );
        }
      })
      .finally(() => {
        if (!isCancelled) setIsBusy(false);
      });
    return () => {
      isCancelled = true;
    };
  }, [apiClient, isEnrolled]);

  async function onSubmit(event: React.FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    if (!isAcceptableCode(code, isEnrolled)) {
      setFieldError(
        isEnrolled
          ? 'Enter the 6-digit code from your authenticator app, or one of your saved backup codes.'
          : 'Enter the 6-digit code from your authenticator app.'
      );
      return;
    }
    setFieldError(null);
    setFormError(null);
    setIsBusy(true);
    try {
      const verification = await apiClient.verifyMfa(code.trim());
      applyMfaVerification(verification);
      onVerified();
    } catch (error) {
      setFormError(describeMfaVerifyError(error));
      if (isSessionError(error)) {
        // The partial token is unusable (expired, revoked, or already gone):
        // drop it locally so the card cannot be retried against nothing, and
        // let the user start over. The api client did not sign us out for a
        // wrong code — only for this.
        void signOut({ isRemote: false });
      }
    } finally {
      setIsBusy(false);
    }
  }

  return (
    <form className="stack" onSubmit={(event) => void onSubmit(event)} noValidate>
      <div>
        <h2 className="auth__title">
          {isEnrolled ? 'Enter your authenticator code' : 'Set up your authenticator'}
        </h2>
        <p className="muted">
          {isEnrolled
            ? 'Admin and coaching accounts require a second factor at sign-in.'
            : 'Add this secret to an authenticator app, then enter the current code to finish signing in.'}
        </p>
      </div>

      {formError ? (
        <div className="banner banner--error" role="alert">
          {formError}
        </div>
      ) : null}

      {enrollment ? (
        <div className="stack">
          <div className="field">
            <span className="field__label">Authenticator secret (otpauth URI)</span>
            <code className="field__control mono" style={{ overflowWrap: 'anywhere' }}>
              {enrollment.totp_uri}
            </code>
          </div>
          <div className="banner banner--warning">
            <span className="banner__title">Save your backup codes now</span>
            <span>Each code works once if you lose your authenticator. They are shown once.</span>
          </div>
          <ul className="mfa-codes" aria-label="Backup codes">
            {enrollment.backup_codes.map((backupCode) => (
              <li key={backupCode}>{backupCode}</li>
            ))}
          </ul>
        </div>
      ) : null}

      <TextField
        label={isEnrolled ? 'Authenticator or backup code' : '6-digit code'}
        inputMode={isEnrolled ? 'text' : 'numeric'}
        autoComplete="one-time-code"
        autoFocus
        // Enrolled accounts may recover with a saved backup code (letters and
        // digits, hyphens optional), so the field is not digit-only there.
        maxLength={isEnrolled ? 24 : 6}
        value={code}
        hint={isEnrolled ? BACKUP_CODE_HINT : undefined}
        error={fieldError}
        onChange={(event) =>
          setCode(
            isEnrolled
              ? event.target.value.replace(/[^0-9a-z-]/gi, '').toUpperCase()
              : event.target.value.replace(/\D/g, '')
          )
        }
      />

      <div className="btn-row">
        <button type="submit" className="btn btn--primary" disabled={isBusy}>
          {isBusy ? 'Checking…' : 'Verify and continue'}
        </button>
        <button type="button" className="btn btn--secondary" onClick={onCancel} disabled={isBusy}>
          Cancel
        </button>
      </div>
      {/* Recovery guidance only — never a self-service reset. Replacing an
          authenticator lives inside a fully signed-in, MFA-verified session
          (Settings → Preferences → Two-factor authentication). A pre-login
          reset would let anyone holding just the password bypass the factor. */}
      {isEnrolled ? (
        <p className="muted auth__mfa-recovery" data-testid="mfa-recovery-help">
          {MFA_RECOVERY_HELP}
        </p>
      ) : null}
    </form>
  );
}
